import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getStoredContext, getStoredSession } from '@/auth/sessionStorage';
import { createOrganisation, queryOrganisations } from '@/features/organisations/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import {
  createProject as createProjectRequest,
  getProjectById,
  queryProjects,
} from '@/features/projects/data';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { buildStorageKeyForNamespace, resolveCacheNamespace } from '@/shared/data/cacheNamespace';
import { gridPaths, invalidateCurrentUserRoles, invalidateGridQuery, queryKeys } from './queryKeys';
import { useInvalidateQueries } from './useInvalidateQueries';
import type { AuthSession } from '@/auth/types';
import type { OrganisationDto, ProjectDto, QueryResult } from '@flowqa/shared';

type UseBootstrapDataArgs = { session: AuthSession | null; enabled?: boolean };
type OrganisationOption = Pick<OrganisationDto, 'id' | 'name' | 'slug' | 'clerkOrganizationId'>;
type ProjectOption = Pick<
  ProjectDto,
  'id' | 'name' | 'slug' | 'organisationId' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'
>;

const ORG_OPTIONS_CACHE_KEY = 'flowqa-org-options-cache';
const PROJECT_OPTIONS_CACHE_KEY = 'flowqa-project-options-cache';
const ORG_FILTER_KEY = 'flowqa-organisation-filter';
const PROJECT_FILTER_KEY = 'flowqa-project-filter';
const BOOTSTRAP_SNAPSHOT_CACHE_KEY = 'flowqa-bootstrap-snapshot-cache';
const bootstrapSnapshotTtlMs = 5 * 60_000;

const buildScopedStorageKey = (baseKey: string, userId?: string | null): string =>
  buildStorageKeyForNamespace(baseKey, resolveCacheNamespace(userId));

type BootstrapSnapshot = {
  savedAt: number;
  organisations: OrganisationOption[];
  projects: ProjectDto[];
};

const hasServerOrganisationContext = (session: AuthSession | null): boolean => {
  if (!session) return false;
  const serverOrganisationId = session.lastOrganisationId ?? session.orgId ?? null;
  return typeof serverOrganisationId === 'string' && serverOrganisationId.trim().length > 0;
};

const writeCachedOrganisations = (
  organisations: OrganisationOption[],
  userId?: string | null,
): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    buildScopedStorageKey(ORG_OPTIONS_CACHE_KEY, userId),
    JSON.stringify(organisations),
  );
};

const readCachedOrganisations = (userId?: string | null): OrganisationOption[] => {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(buildScopedStorageKey(ORG_OPTIONS_CACHE_KEY, userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is OrganisationOption =>
        typeof item === 'object' &&
        item !== null &&
        typeof (item as { id?: unknown }).id === 'string' &&
        typeof (item as { name?: unknown }).name === 'string' &&
        typeof (item as { slug?: unknown }).slug === 'string' &&
        ((item as { clerkOrganizationId?: unknown }).clerkOrganizationId === null ||
          typeof (item as { clerkOrganizationId?: unknown }).clerkOrganizationId === 'string'),
    );
  } catch {
    return [];
  }
};

const toProjectOption = (project: ProjectDto): ProjectOption => ({
  id: project.id,
  name: project.name,
  slug: project.slug,
  organisationId: project.organisationId,
  badgeChars: project.badgeChars,
  badgeColor: project.badgeColor,
  badgeEnabled: project.badgeEnabled,
});

const upsertProjectById = (items: ProjectDto[], nextItem: ProjectDto): ProjectDto[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }
  const next = [...items];
  next[existingIndex] = nextItem;
  return next;
};

const upsertProjectOptionById = (
  items: ProjectOption[],
  nextItem: ProjectOption,
): ProjectOption[] => {
  const existingIndex = items.findIndex((item) => item.id === nextItem.id);
  if (existingIndex === -1) {
    return [...items, nextItem];
  }
  const next = [...items];
  next[existingIndex] = nextItem;
  return next;
};

const mergeProjectsById = (items: ProjectDto[]): ProjectDto[] => {
  const merged = new Map<string, ProjectDto>();
  items.forEach((item) => {
    merged.set(item.id, item);
  });
  return [...merged.values()];
};

const upsertProjectQueryResult = (
  result: QueryResult<ProjectDto> | undefined,
  nextProject: ProjectDto,
): QueryResult<ProjectDto> => {
  const nextItems = upsertProjectById(result?.items ?? [], nextProject);
  return {
    items: nextItems,
    total: nextItems.length,
    page: result?.page ?? 1,
    pageSize: result?.pageSize ?? Math.max(nextItems.length, 1),
  };
};

const readProjectOptionsCacheMap = (userId?: string | null): Record<string, ProjectOption[]> => {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY, userId));
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return {};
    const entries = Object.entries(parsed);
    const validEntries = entries
      .map(([key, value]): [string, ProjectOption[]] | null => {
        if (!Array.isArray(value)) return null;
        const projects = value.flatMap((item): ProjectOption[] => {
          if (typeof item !== 'object' || item === null) return [];
          const badgeEnabledRaw = (item as { badgeEnabled?: unknown }).badgeEnabled;
          const badgeEnabled = typeof badgeEnabledRaw === 'boolean' ? badgeEnabledRaw : true;
          const organisationId = (item as { organisationId?: unknown }).organisationId;
          if (typeof (item as { id?: unknown }).id !== 'string') return [];
          if (typeof (item as { name?: unknown }).name !== 'string') return [];
          if (typeof (item as { slug?: unknown }).slug !== 'string') return [];
          if (typeof (item as { badgeChars?: unknown }).badgeChars !== 'string') return [];
          if (typeof (item as { badgeColor?: unknown }).badgeColor !== 'string') return [];
          if (organisationId !== null && typeof organisationId !== 'string') return [];
          return [
            {
              id: (item as { id: string }).id,
              name: (item as { name: string }).name,
              slug: (item as { slug: string }).slug,
              organisationId: organisationId as string | null,
              badgeChars: (item as { badgeChars: string }).badgeChars,
              badgeColor: (item as { badgeColor: ProjectDto['badgeColor'] }).badgeColor,
              badgeEnabled,
            },
          ];
        });
        return [key, projects];
      })
      .filter((entry): entry is [string, ProjectOption[]] => entry !== null);
    return Object.fromEntries(validEntries);
  } catch {
    return {};
  }
};

const getProjectCacheKey = (organisationId: string | null): string => organisationId ?? 'personal';

const writeCachedProjectOptions = (
  organisationId: string | null,
  projects: ProjectOption[],
  userId?: string | null,
): void => {
  if (typeof window === 'undefined') return;
  const map = readProjectOptionsCacheMap(userId);
  map[getProjectCacheKey(organisationId)] = projects;
  window.localStorage.setItem(
    buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY, userId),
    JSON.stringify(map),
  );
};

const readCachedProjectOptions = (
  organisationId: string | null,
  userId?: string | null,
): ProjectOption[] => {
  const map = readProjectOptionsCacheMap(userId);
  return map[getProjectCacheKey(organisationId)] ?? [];
};

const isOrganisationOption = (value: unknown): value is OrganisationOption =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  typeof (value as { name?: unknown }).name === 'string' &&
  typeof (value as { slug?: unknown }).slug === 'string';

const isProjectDto = (value: unknown): value is ProjectDto =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { id?: unknown }).id === 'string' &&
  typeof (value as { name?: unknown }).name === 'string' &&
  typeof (value as { slug?: unknown }).slug === 'string' &&
  typeof (value as { badgeChars?: unknown }).badgeChars === 'string' &&
  typeof (value as { badgeColor?: unknown }).badgeColor === 'string' &&
  typeof (value as { badgeEnabled?: unknown }).badgeEnabled === 'boolean' &&
  ((value as { organisationId?: unknown }).organisationId === null ||
    typeof (value as { organisationId?: unknown }).organisationId === 'string');

const writeBootstrapSnapshot = (snapshot: BootstrapSnapshot, userId?: string | null): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    buildScopedStorageKey(BOOTSTRAP_SNAPSHOT_CACHE_KEY, userId),
    JSON.stringify(snapshot),
  );
};

const clearBootstrapSnapshot = (userId?: string | null): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(buildScopedStorageKey(BOOTSTRAP_SNAPSHOT_CACHE_KEY, userId));
};

const readBootstrapSnapshot = (userId?: string | null): BootstrapSnapshot | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(
    buildScopedStorageKey(BOOTSTRAP_SNAPSHOT_CACHE_KEY, userId),
  );
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const savedAt = (parsed as { savedAt?: unknown }).savedAt;
    const organisations = (parsed as { organisations?: unknown }).organisations;
    const projects = (parsed as { projects?: unknown }).projects;
    if (typeof savedAt !== 'number') return null;
    if (!Array.isArray(organisations) || !Array.isArray(projects)) return null;
    const validOrganisations = organisations.filter(isOrganisationOption);
    const validProjects = projects.filter(isProjectDto);
    if (validOrganisations.length !== organisations.length) return null;
    if (validProjects.length !== projects.length) return null;
    return {
      savedAt,
      organisations: validOrganisations,
      projects: validProjects,
    };
  } catch {
    return null;
  }
};

const readFreshBootstrapSnapshot = (userId?: string | null): BootstrapSnapshot | null => {
  const snapshot = readBootstrapSnapshot(userId);
  if (snapshot === null) return null;
  return Date.now() - snapshot.savedAt <= bootstrapSnapshotTtlMs ? snapshot : null;
};

const writeBootstrapProjectOptionCaches = (
  projects: ProjectDto[],
  userId?: string | null,
): void => {
  writeCachedProjectOptions(null, projects.map(toProjectOption), userId);
  const organisationIds = Array.from(
    new Set(
      projects
        .map((project) => project.organisationId)
        .filter((organisationId): organisationId is string => organisationId !== null),
    ),
  );
  organisationIds.forEach((organisationId) => {
    writeCachedProjectOptions(
      organisationId,
      projects.filter((project) => project.organisationId === organisationId).map(toProjectOption),
      userId,
    );
  });
};

const writeProjectStateCaches = ({
  projects,
  organisations,
  userId,
}: {
  projects: ProjectDto[];
  organisations: OrganisationOption[];
  userId?: string | null;
}): void => {
  writeBootstrapProjectOptionCaches(projects, userId);
  writeBootstrapSnapshot(
    {
      savedAt: Date.now(),
      organisations,
      projects,
    },
    userId,
  );
};

export const useBootstrapData = ({
  session,
  enabled = true,
}: UseBootstrapDataArgs): {
  projectsLoaded: boolean;
  projects: ProjectDto[];
  organisations: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug' | 'clerkOrganizationId'>>;
  visibleProjects: ProjectDto[];
  visibleProjectOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'>
  >;
  createProject: (
    input: {
      name: string;
      badgeEnabled?: boolean;
      badgeChars?: ProjectDto['badgeChars'];
      badgeColor?: ProjectDto['badgeColor'];
    },
    options?: { setAsCurrent?: boolean },
  ) => Promise<ProjectDto | null>;
  createOrg: (name: string) => Promise<OrganisationDto | null>;
} => {
  const { notify } = useNotifications();
  const { selectedOrganisationId, setSelectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const bootstrapUserId = session?.userId ?? getStoredSession()?.userId ?? null;
  const initialBootstrapSnapshot =
    bootstrapUserId !== null && hasServerOrganisationContext(session)
      ? readFreshBootstrapSnapshot(bootstrapUserId)
      : null;
  const [projects, setProjects] = useState<ProjectDto[]>(initialBootstrapSnapshot?.projects ?? []);
  const [cachedProjectOptions, setCachedProjectOptions] = useState<ProjectOption[]>(
    () =>
      initialBootstrapSnapshot?.projects.map(toProjectOption) ??
      readCachedProjectOptions(null, bootstrapUserId),
  );
  const [organisations, setOrganisations] = useState<OrganisationOption[]>(
    () => initialBootstrapSnapshot?.organisations ?? readCachedOrganisations(bootstrapUserId),
  );
  const [hasLoadedProjects, setHasLoadedProjects] = useState(initialBootstrapSnapshot !== null);
  const loadSeq = useRef(0);
  const lastUserIdRef = useRef<string | null>(null);
  const invalidateQueries = useInvalidateQueries();
  const queryClient = useQueryClient();

  useEffect(() => {
    const nextUserId = session?.userId ?? null;
    if (lastUserIdRef.current === nextUserId) return;
    lastUserIdRef.current = nextUserId;
    const snapshot =
      nextUserId !== null && hasServerOrganisationContext(session)
        ? readFreshBootstrapSnapshot(nextUserId)
        : null;
    setProjects(snapshot?.projects ?? []);
    setOrganisations(snapshot?.organisations ?? []);
    setCachedProjectOptions(snapshot?.projects.map(toProjectOption) ?? []);
    setHasLoadedProjects(snapshot !== null);
    const nextOrganisationId = hasServerOrganisationContext(session)
      ? (session?.lastOrganisationId ?? session?.orgId ?? null)
      : null;
    const nextProjectId = hasServerOrganisationContext(session)
      ? (session?.lastProjectId ?? getStoredContext().lastProjectId ?? null)
      : null;
    setSelectedOrganisationId(nextOrganisationId);
    setSelectedProjectId(nextProjectId);
  }, [session?.userId, setSelectedOrganisationId, setSelectedProjectId]);

  useEffect(() => {
    if (!session?.userId) return;
    setCachedProjectOptions(readCachedProjectOptions(selectedOrganisationId, session.userId));
  }, [selectedOrganisationId, session?.userId]);

  useEffect(() => {
    const handleOrgCreated = (event: Event): void => {
      const detail = (event as CustomEvent<OrganisationDto>).detail;
      if (!detail.id) return;
      setOrganisations((prev) => {
        const next = prev.some((org) => org.id === detail.id)
          ? prev
          : [
              ...prev,
              {
                id: detail.id,
                name: detail.name,
                slug: detail.slug,
                clerkOrganizationId: detail.clerkOrganizationId,
              },
            ];
        writeCachedOrganisations(next, session?.userId);
        return next;
      });
    };
    const handleOrgUpdated = (event: Event): void => {
      const detail = (
        event as CustomEvent<{
          id: string;
          name: string;
          clerkOrganizationId?: string | null;
        }>
      ).detail;
      if (!detail.id) return;
      setOrganisations((prev) => {
        const next = prev.map((org) =>
          org.id === detail.id
            ? {
                ...org,
                name: detail.name,
                clerkOrganizationId: detail.clerkOrganizationId ?? org.clerkOrganizationId,
              }
            : org,
        );
        writeCachedOrganisations(next, session?.userId);
        return next;
      });
    };
    window.addEventListener('flowqa-org-created', handleOrgCreated);
    window.addEventListener('flowqa-org-updated', handleOrgUpdated);
    return () => {
      window.removeEventListener('flowqa-org-created', handleOrgCreated);
      window.removeEventListener('flowqa-org-updated', handleOrgUpdated);
    };
  }, [session?.userId]);

  useEffect(() => {
    const handleProjectCreated = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectDto>).detail;
      if (!detail.id) return;
      setProjects((prev) =>
        prev.some((proj) => proj.id === detail.id) ? prev : [...prev, detail],
      );
      if (detail.organisationId !== selectedOrganisationId) return;
      setCachedProjectOptions((prev) => {
        const next = prev.some((project) => project.id === detail.id)
          ? prev
          : [...prev, toProjectOption(detail)];
        writeCachedProjectOptions(selectedOrganisationId, next, session?.userId);
        return next;
      });
    };
    window.addEventListener('flowqa-project-created', handleProjectCreated);
    return () => {
      window.removeEventListener('flowqa-project-created', handleProjectCreated);
    };
  }, [selectedOrganisationId, session?.userId]);

  useEffect(() => {
    const handleProjectUpdated = (event: Event): void => {
      const detail = (event as CustomEvent<ProjectDto>).detail;
      if (!detail.id) return;
      setProjects((prev) =>
        prev.map((project) => (project.id === detail.id ? { ...project, ...detail } : project)),
      );
      if (detail.organisationId !== selectedOrganisationId) return;
      setCachedProjectOptions((prev) => {
        const next = prev.map((project) =>
          project.id === detail.id ? toProjectOption(detail) : project,
        );
        writeCachedProjectOptions(selectedOrganisationId, next, session?.userId);
        return next;
      });
    };
    window.addEventListener('flowqa-project-updated', handleProjectUpdated);
    return () => {
      window.removeEventListener('flowqa-project-updated', handleProjectUpdated);
    };
  }, [selectedOrganisationId, session?.userId]);

  useEffect(() => {
    const handleProjectRemoved = (event: Event): void => {
      const detail = (event as CustomEvent<{ id: string }>).detail;
      if (!detail.id) return;
      setProjects((prev) => {
        const next = prev.filter((project) => project.id !== detail.id);
        writeProjectStateCaches({
          projects: next,
          organisations,
          userId: session?.userId,
        });
        return next;
      });
      setCachedProjectOptions((prev) => {
        const next = prev.filter((project) => project.id !== detail.id);
        writeCachedProjectOptions(selectedOrganisationId, next, session?.userId);
        return next;
      });
      if (selectedProjectId === detail.id) {
        setSelectedProjectId(null);
      }
    };
    window.addEventListener('flowqa-project-removed', handleProjectRemoved);
    return () => {
      window.removeEventListener('flowqa-project-removed', handleProjectRemoved);
    };
  }, [
    organisations,
    selectedOrganisationId,
    selectedProjectId,
    session?.userId,
    setSelectedProjectId,
  ]);

  useEffect(() => {
    const handleOrgRemoved = (event: Event): void => {
      const detail = (event as CustomEvent<{ reason?: string } | undefined>).detail;
      if (detail?.reason === 'deleted_by_user') {
        return;
      }
      notify({ message: 'You no longer have access to that workspace', tone: 'orange' });
    };
    window.addEventListener('flowqa-org-removed', handleOrgRemoved);
    return () => {
      window.removeEventListener('flowqa-org-removed', handleOrgRemoved);
    };
  }, [notify]);

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (!enabled) return;
      const runId = ++loadSeq.current;
      setHasLoadedProjects(false);
      if (!session?.userId) {
        setProjects([]);
        setOrganisations([]);
        setCachedProjectOptions([]);
        setHasLoadedProjects(true);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(buildScopedStorageKey(ORG_OPTIONS_CACHE_KEY));
          window.localStorage.removeItem(buildScopedStorageKey(PROJECT_OPTIONS_CACHE_KEY));
          window.localStorage.removeItem(buildScopedStorageKey(ORG_FILTER_KEY));
          window.localStorage.removeItem(buildScopedStorageKey(PROJECT_FILTER_KEY));
        }
        clearBootstrapSnapshot();
        return;
      }
      const freshSnapshot = hasServerOrganisationContext(session)
        ? readFreshBootstrapSnapshot(session.userId)
        : null;
      const persistedSnapshot = hasServerOrganisationContext(session)
        ? readBootstrapSnapshot(session.userId)
        : null;
      if (freshSnapshot !== null) {
        setOrganisations(freshSnapshot.organisations);
        setProjects(freshSnapshot.projects);
        setCachedProjectOptions(readCachedProjectOptions(selectedOrganisationId, session.userId));
        setHasLoadedProjects(true);
        queryClient.setQueryData(
          queryKeys.organisations(session.userId),
          freshSnapshot.organisations,
        );
        queryClient.setQueryData(queryKeys.projects(null), {
          items: freshSnapshot.projects.filter((project) => project.organisationId === null),
          total: freshSnapshot.projects.filter((project) => project.organisationId === null).length,
        });
        freshSnapshot.organisations.forEach((organisation) => {
          const orgProjects = freshSnapshot.projects.filter(
            (project) => project.organisationId === organisation.id,
          );
          queryClient.setQueryData(queryKeys.projects(organisation.id), {
            items: orgProjects,
            total: orgProjects.length,
          });
        });
      }
      try {
        const orgs = await queryOrganisations();
        const projectRequests = [
          { orgId: null, promise: queryProjects(null) },
          ...orgs.items.map((organisation) => ({
            orgId: organisation.id,
            promise: queryProjects(organisation.id),
          })),
        ];
        const projectPromises: Array<ReturnType<typeof queryProjects>> = [];
        projectRequests.forEach((request) => {
          projectPromises.push(request.promise);
        });
        const projectResults = await Promise.allSettled(projectPromises);
        if (runId !== loadSeq.current) return;
        setOrganisations(orgs.items);
        writeCachedOrganisations(orgs.items, session.userId);
        const successfulProjectResults = projectResults.flatMap((result) =>
          result.status === 'fulfilled' ? [result.value] : [],
        );
        const mergedProjects = mergeProjectsById(
          successfulProjectResults.flatMap((result) => result.items),
        );
        const hasRejectedProjectQuery = projectResults.some(
          (result) => result.status === 'rejected',
        );
        const rememberedProjectId =
          selectedProjectId ?? session.lastProjectId ?? getStoredContext().lastProjectId ?? null;
        const rememberedOrganisationId =
          session.lastOrganisationId ?? session.orgId ?? selectedOrganisationId ?? null;
        const rememberedProject =
          rememberedProjectId !== null &&
          !mergedProjects.some((project) => project.id === rememberedProjectId)
            ? await getProjectById(
                rememberedProjectId,
                rememberedOrganisationId ?? undefined,
              ).catch(() => null)
            : null;
        const refreshedProjects = rememberedProject
          ? mergeProjectsById([...mergedProjects, rememberedProject])
          : mergedProjects;
        const shouldPreserveSnapshotProjects =
          refreshedProjects.length === 0 &&
          persistedSnapshot !== null &&
          persistedSnapshot.projects.length > 0 &&
          (hasRejectedProjectQuery ||
            (rememberedProjectId !== null &&
              persistedSnapshot.projects.some((project) => project.id === rememberedProjectId)));
        const resolvedProjects = shouldPreserveSnapshotProjects
          ? persistedSnapshot.projects
          : refreshedProjects;
        setProjects(resolvedProjects);
        const nextProjectOptions = resolvedProjects.map(toProjectOption);
        setCachedProjectOptions(nextProjectOptions);
        writeBootstrapProjectOptionCaches(resolvedProjects, session.userId);
        writeBootstrapSnapshot(
          {
            savedAt: Date.now(),
            organisations: orgs.items,
            projects: resolvedProjects,
          },
          session.userId,
        );
        queryClient.setQueryData(queryKeys.organisations(session.userId), orgs.items);
        projectResults.forEach((result, index) => {
          if (result.status !== 'fulfilled') {
            return;
          }
          const orgId = projectRequests[index]?.orgId ?? null;
          queryClient.setQueryData(queryKeys.projects(orgId), result.value);
        });
        setHasLoadedProjects(true);
      } catch {
        if (runId !== loadSeq.current) return;
        const fallbackSnapshot = readBootstrapSnapshot(session.userId);
        const fallbackProjects = fallbackSnapshot?.projects ?? [];
        const fallbackOrganisations =
          fallbackSnapshot?.organisations ?? readCachedOrganisations(session.userId);
        setProjects(fallbackProjects);
        setOrganisations(fallbackOrganisations);
        setCachedProjectOptions(
          fallbackSnapshot?.projects
            .filter((project) => project.organisationId === selectedOrganisationId)
            .map(toProjectOption) ??
            readCachedProjectOptions(selectedOrganisationId, session.userId),
        );
        setHasLoadedProjects(true);
      }
    };
    void loadProjects();
  }, [enabled, queryClient, selectedOrganisationId, session?.userId]);

  const createProject = useCallback(
    async (
      input: {
        name: string;
        badgeEnabled?: boolean;
        badgeChars?: ProjectDto['badgeChars'];
        badgeColor?: ProjectDto['badgeColor'];
      },
      options?: { setAsCurrent?: boolean },
    ): Promise<ProjectDto | null> => {
      if (!session?.userId) return null;
      const created = await createProjectRequest(input, selectedOrganisationId);
      const nextProjects = upsertProjectById(projects, created);
      setProjects(nextProjects);
      queryClient.setQueryData<QueryResult<ProjectDto>>(
        queryKeys.projects(created.organisationId ?? null),
        (prev) => upsertProjectQueryResult(prev, created),
      );
      if (created.organisationId === selectedOrganisationId) {
        setCachedProjectOptions((prev) => {
          const next = upsertProjectOptionById(prev, toProjectOption(created));
          writeCachedProjectOptions(selectedOrganisationId, next, session.userId);
          return next;
        });
      }
      writeProjectStateCaches({
        projects: nextProjects,
        organisations,
        userId: session.userId,
      });
      setHasLoadedProjects(true);
      if (options?.setAsCurrent !== false) {
        setSelectedProjectId(created.id);
      }
      notify({ message: 'Project created', tone: 'green' });
      await invalidateQueries([
        invalidateGridQuery(gridPaths.projects),
        invalidateCurrentUserRoles(),
      ]);
      return created;
    },
    [
      invalidateQueries,
      notify,
      organisations,
      projects,
      queryClient,
      selectedOrganisationId,
      session?.userId,
      setSelectedProjectId,
    ],
  );

  const createOrg = useCallback(
    async (name: string): Promise<OrganisationDto | null> => {
      if (!session?.userId) return null;
      const created = await createOrganisation({ name });
      notify({ message: 'Workspace created', tone: 'green' });
      const orgs = await queryOrganisations();
      setOrganisations(orgs.items);
      writeCachedOrganisations(orgs.items, session.userId);
      setSelectedOrganisationId(created.id);
      setSelectedProjectId(null);
      await invalidateQueries([
        invalidateGridQuery(gridPaths.organisations),
        invalidateGridQuery(gridPaths.projects),
        invalidateGridQuery(gridPaths.users),
        invalidateCurrentUserRoles(),
      ]);
      return created;
    },
    [invalidateQueries, notify, session?.userId, setSelectedOrganisationId, setSelectedProjectId],
  );

  const visibleProjects = useMemo(() => {
    if (selectedOrganisationId === null) {
      return projects;
    }
    return projects.filter((p) => p.organisationId === selectedOrganisationId);
  }, [projects, selectedOrganisationId]);

  const visibleProjectOptions = useMemo(() => {
    const currentOptions = visibleProjects.map((project) => ({
      id: project.id,
      name: project.name,
      slug: project.slug,
      badgeChars: project.badgeChars,
      badgeColor: project.badgeColor,
      badgeEnabled: project.badgeEnabled,
    }));
    if (currentOptions.length > 0 || hasLoadedProjects) {
      return currentOptions;
    }
    return cachedProjectOptions.map((project) => ({
      id: project.id,
      name: project.name,
      slug: project.slug,
      badgeChars: project.badgeChars,
      badgeColor: project.badgeColor,
      badgeEnabled: project.badgeEnabled,
    }));
  }, [cachedProjectOptions, hasLoadedProjects, visibleProjects]);

  return {
    projectsLoaded: hasLoadedProjects,
    projects,
    organisations,
    visibleProjects,
    visibleProjectOptions,
    createProject,
    createOrg,
  };
};
